import {
  Box,
  Button,
  Center,
  Flex,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import MultiSelectWithLimitDisplayed from '@v2/commons/components/MultiSelectWithLimitDisplayed/MultiSelectWithLimitDisplayed'
import PhoneNumberSelect from '@v2/commons/components/PhoneNumberSelect/PhoneNumberSelect.component'
import { LANGUAGES_FOR_LOCATIONS } from '@v2/commons/config'
import React from 'react'

import { LocationDto } from 'src/api/generated'

import {
  ICreateClinicHook,
  useCreateLocation,
} from '../../hooks/useCreateLocation.hook'
import {
  IUpdateClinicHook,
  useUpdateLocation,
} from '../../hooks/useUpdateLocation.hook'

interface CreateLocationModalProps {
  clinicId: string
  clinicName: string
  location: LocationDto | null
}

const CreateUpdateLocationModalContent = ({
  clinicId,
  clinicName,
  location,
}: CreateLocationModalProps) => {
  const { form, onSubmit, isLoading, ALL_STATES } = location
    ? useUpdateLocation({ clinicId, clinicName, location })
    : (useCreateLocation({ clinicId, clinicName }) as IUpdateClinicHook &
        ICreateClinicHook)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <Center>
        <Text fz="h3" fw="bold">
          {location ? 'Update Location' : 'Create New Location'}
        </Text>
      </Center>
      <Flex
        style={{ borderBottom: '1px solid var(--mantine-color-blue-5)' }}
        justify="space-between"
        align="flex-start"
      >
        <Text fw={700} c="blue.5">
          Basic Information
        </Text>
      </Flex>
      <Box p="lg">
        <SimpleGrid mb="lg" cols={2} spacing="lg">
          <TextInput
            key={form.key('clinicName')}
            label="Clinic Name"
            placeholder="Enter Clinic Name"
            withAsterisk
            disabled
            {...form.getInputProps('clinicName')}
          />

          <TextInput
            key={form.key('name')}
            label="Location Name"
            placeholder="Enter zip code"
            withAsterisk
            {...form.getInputProps('name')}
          />

          <MultiSelectWithLimitDisplayed
            maxDisplayedValues={2}
            key={form.key('languages')}
            label="Languages Spoken"
            placeholder="Select status"
            data={LANGUAGES_FOR_LOCATIONS}
            withAsterisk
            {...form.getInputProps('languages')}
          />

          <PhoneNumberSelect
            initialCountryCode="US"
            {...form.getInputProps('phoneNumber')}
            key={form.key('phoneNumber')}
            initialValue={form.getInputProps('phoneNumber').defaultValue}
            withAsterisk={true}
          />

          <TextInput
            key={form.key('email')}
            label="Email Address"
            placeholder="Email Address"
            {...form.getInputProps('email')}
          />
        </SimpleGrid>
      </Box>

      <Flex
        style={{ borderBottom: '1px solid var(--mantine-color-blue-5)' }}
        justify="space-between"
        align="flex-start"
      >
        <Text fw={700} c="blue.5">
          Address Information
        </Text>
      </Flex>
      <Box p="lg">
        <SimpleGrid mb="lg" cols={2} spacing="lg">
          <TextInput
            key={form.key('address.streetAddress')}
            label="Street Address"
            placeholder="Street or Ave"
            withAsterisk
            {...form.getInputProps('address.streetAddress')}
          />

          <TextInput
            key={form.key('address.city')}
            label="City"
            placeholder="City"
            withAsterisk
            {...form.getInputProps('address.city')}
          />

          <Select
            key={form.key('address.state')}
            label="State"
            placeholder="Select state"
            data={ALL_STATES}
            withAsterisk
            searchable
            {...form.getInputProps('address.state')}
          />

          <TextInput
            key={form.key('address.zipCode')}
            label="Zip Code"
            placeholder="Zip Code"
            withAsterisk
            {...form.getInputProps('address.zipCode')}
          />
        </SimpleGrid>
      </Box>

      <Flex
        style={{ borderBottom: '1px solid var(--mantine-color-blue-5)' }}
        justify="space-between"
        align="flex-start"
      >
        <Text fw={700} c="blue.5">
          Access & Hours Information
        </Text>
      </Flex>
      <Box p="lg">
        <Stack mb="lg">
          <Textarea
            key={form.key('businessDays')}
            label="Location Operation Days & Hours"
            placeholder="Enter the corresponding hours of operation"
            withAsterisk
            description="Please specify the days the location is open and the corresponding hours of operation. If hours differ by day, please mention them. Also, be sure to include the time zone."
            {...form.getInputProps('businessDays')}
          />

          <TextInput
            key={form.key('address.parkingSpace')}
            label="Parking Space"
            placeholder="Parking Space"
            {...form.getInputProps('address.parkingSpace')}
          />
        </Stack>
      </Box>

      <Box>
        <Flex justify="space-between" align="center" mt="xl">
          <Button variant="outline" onClick={() => modals.closeAll()}>
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {location ? 'Update Location' : 'Create New Location'}
          </Button>
        </Flex>
      </Box>
    </form>
  )
}

export const openCreateUpdateLocationModal = (
  clinicId: string,
  clinicName: string,
  location: LocationDto | null,
) => {
  return modals.open({
    centered: true,
    size: 'lg',
    withCloseButton: true,
    children: (
      <CreateUpdateLocationModalContent
        clinicId={clinicId}
        clinicName={clinicName}
        location={location}
      />
    ),
  })
}
